import type { IAuthData } from '@skedulo/horizon-types'

class ApiResponseError extends Error {
  constructor(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Unused params. Should be improved, assume they are wanted.
    // @ts-ignore
    private url: string,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Unused params. Should be improved, assume they are wanted.
    // @ts-ignore
    private status: number,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- Unused params. Should be improved, assume they are wanted.
    // @ts-ignore
    private body: any,
  ) {
    super(`${JSON.stringify(body)}`)
  }
}

export interface ICustomOptions {
  rawResponse?: boolean
  queryParams?: Record<string, string>
}

type GraphQlData = { [key: string]: any; [index: number]: never }
interface GraphQlResponse<T extends GraphQlData> {
  data: T
  errors?: Array<{ message: string }>
}

/**
 *  Fetch Api Client to call skedulo's BE service inside horizon component
 *  provide helper for service's root path + jwt
 */
export class FetchApiClient {
  private readonly auth: IAuthData
  private readonly baseEndpoint: string

  constructor(baseEndpoint: string, auth: IAuthData) {
    this.auth = auth
    // enforce baseEndpoint to end with single `/`
    this.baseEndpoint = baseEndpoint.replace(/\/?$/, '/')
  }

  private get apiToken() {
    return this.auth.skedApiAccessToken
  }

  private createRequest(path: string, requestInit: RequestInit): Request {
    const request = new Request(
      new URL(
        path.replace(/^\/*/, ''), // strip all leading `/`
        this.baseEndpoint,
      ).toString(),
      requestInit,
    )
    request.headers.set('authorization', `Bearer ${this.apiToken}`)

    return request
  }

  // return path with any query params added
  private addQueryParams(path: string, queryParams?: Record<string, string>) {
    if (queryParams && Object.keys(queryParams).length > 0) {
      const queryString = new URLSearchParams(queryParams).toString()
      const separator = path.includes('?') ? '&' : '?'
      return `${path}${separator}${queryString}`
    }
    return path
  }

  /**
   * bare minimum fetch wrapper
   * used when want to manual handle the response object
   * example when response's body is not json
   */
  fetch(path: string, info: RequestInit = {}): Promise<Response> {
    return fetch(this.createRequest(path, info))
  }

  /**
   * Generic Fetch Request with common response handler, where Response is in JSON format
   */
  async fetchJson<T>(
    path: string,
    info: RequestInit = {},
    customOptions?: ICustomOptions,
  ): Promise<T> {
    path = this.addQueryParams(path, customOptions?.queryParams)
    const response = await this.fetch(path, info)
    if (response.ok) {
      if (customOptions?.rawResponse) {
        return (await response.json()) as unknown as T
      }

      // this required the endpoint to response in {result} format
      // this is common convention for Skedulo's BE
      // https://skedulo.atlassian.net/wiki/spaces/EN/pages/12189700/HTTP+REST+and+JSON+API+Convention+for+Web+Services#Results
      // https://github.com/Skedulo/phoenix/blob/59df0207fc290dd9ce4358fb42c7c04768f6939c/frontend/src/services/APICommunicationService.ts#L264
      const { result } = (await response.json()) as { result: T }
      return result
    }
    throw new ApiResponseError(
      response.url,
      response.status,
      await response.json(),
    )
  }

  /**
   * Fetch with GET method
   */
  get<T>(
    path: string,
    { body: omitted, ...info }: RequestInit = {},
    customOptions?: ICustomOptions,
  ): Promise<T> {
    // body is omitted for GET base on fetch standard
    return this.fetchJson<T>(
      path,
      {
        ...info,
        method: 'GET',
      },
      customOptions,
    )
  }

  /**
   * Fetch with POST method
   */
  post<T>(
    path: string,
    info: RequestInit = {},
    customOptions?: ICustomOptions,
  ): Promise<T> {
    return this.fetchJson(
      path,
      {
        ...info,
        method: 'POST',
      },
      customOptions,
    )
  }

  /**
   * Fetch with PUT method
   */
  put<T>(
    path: string,
    info: RequestInit = {},
    customOptions?: ICustomOptions,
  ): Promise<T> {
    return this.fetchJson(
      path,
      {
        ...info,
        method: 'PUT',
      },
      customOptions,
    )
  }

  /**
   * Fetch with DELETE method
   */
  delete<T>(
    path: string,
    info: RequestInit = {},
    customOptions?: ICustomOptions,
  ): Promise<T> {
    return this.fetchJson(
      path,
      {
        ...info,
        method: 'DELETE',
      },
      customOptions,
    )
  }

  /**
   * GraphQL Fetch Request
   * Note: This method automatically sets the 'X-Skedulo-Read-Only': 'true' header for read-only operations
   * @param query - GraphQL query
   * @param variables - Filters or variables to pass to the query
   * @returns Promise of GraphQL response
   */
  async graphQLFetch<T extends GraphQlData>({
    query,
    variables = {},
  }: {
    query: string
    variables: any
  }): Promise<T> {
    // Check if the query is a mutation by looking for the 'mutation' keyword
    const isMutation = /^\s*mutation\s/i.test(query.trim())

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Only add read-only header for non-mutation operations
    if (!isMutation) {
      headers['X-Skedulo-Read-Only'] = 'true'
    }

    const res = await fetch(
      this.createRequest('graphql/graphql', {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables }),
      }),
    )
    if (!res.ok) {
      throw new ApiResponseError(res.url, res.status, res.statusText)
    }

    const graphQlRes: GraphQlResponse<T> = await res.json()
    if (graphQlRes.errors) {
      throw new Error(graphQlRes.errors.map((err) => err.message).join('\n'))
    }

    return graphQlRes.data
  }
}
